import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { PageContainer } from "../components/Container/PageContainer";
import { getCollection } from "../utils/pureDataFetch";
import { Stack, Flex, Button } from "@mantine/core";
import { useTranslation } from "react-i18next";
import CollectionTitleSection from "../components/CollectionTitleSection";
import CollectionContentOfTable from "../components/CollectionContentOfTable";
import CollectionThreadContent from "../components/CollectionThreadContent";
import UpdateCollectionModal from "../components/modal/UpdateCollectionModal";
import { updateCollection, deleteCollection } from "../utils/pureDataFetch";
import { useNavigate } from "react-router-dom";
import { CardContainerRetro } from "../components/Container/CardContainerRetro";
import { CollectionKeyMetricSummarySection } from "../template/metric/CollectionKeyMetricSummarySection";
import colorsCustom from "../designTokens/colorsCustom";
import { useThreadsState } from "../contexts/ThreadsContext";
import { useIsMobile } from "../hooks/useIsMobile";

const CollectionPage = ({ isMe }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { collectionId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const threadID = queryParams.get("threadID");
  const isMobile = useIsMobile("xl");
  const [collection, setCollection] = useState(null);
  const [selectedThread, setSelectedThread] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedThreads, setSelectedThreads] = useState([]);
  const [collectionList, setCollectionList] = useState([]);
  const [metrics, setMetrics] = useState({
    views: 0,
    likes: 0,
    replies: 0,
    reposts: 0,
  });
  const { lifetimeThreadsList } = useThreadsState();

  useEffect(() => {
    console.log("collectionId", collectionId);
    getCollection(collectionId).then((collection) => {
      console.log("collection", collection);
      setCollection(collection);

      console.log("threadID", threadID);
      // Set the selectedThread based on threadID from URL
      const initialThread =
        collection.threadList.find((thread) => thread.id === threadID) ||
        collection.threadList[0];
      setSelectedThread(initialThread);

      // For update collection
      setTitle(collection.title);
      setDescription(collection.description);
      setSelectedThreads(collection.threadList.map((thread) => thread.id));
      setCollectionList(collection.threadList);

      // Calculate total metrics
      const totalMetrics = collection.threadList.reduce(
        (acc, thread) => {
          acc.views += thread.views || 0;
          acc.likes += thread.likes || 0;
          acc.replies += thread.replies || 0;
          acc.reposts += thread.reposts || 0;
          return acc;
        },
        { views: 0, likes: 0, replies: 0, reposts: 0 }
      );

      setMetrics(totalMetrics);
    });
  }, [collectionId, threadID]);

  const handleThreadSelect = (threadId) => {
    setSelectedThreads((prev) =>
      prev.includes(threadId)
        ? prev.filter((id) => id !== threadId)
        : [...prev, threadId]
    );
  };

  useEffect(() => {
    if (isMe) {
      setCollectionList(() => {
        console.log(selectedThreads);
        return lifetimeThreadsList.filter((thread) =>
          selectedThreads.includes(thread.id)
        );
      });
    }
  }, [selectedThreads]);

  const handleRemoveThread = (threadId) => {
    setSelectedThreads((prev) => prev.filter((id) => id !== threadId));
    setCollectionList((prev) =>
      prev.filter((thread) => thread.id !== threadId)
    );
  };

  const handleCreateCollection = async (collectionData) => {
    try {
      const newCollection = await updateCollection(
        collectionId,
        collectionData
      );
      console.log("newCollection", newCollection);
      setCollection(newCollection);
      setModalOpen(false); // Close the modal after creation
    } catch (error) {
      console.error("Error creating collection:", error);
    }
  };

  const handleDeleteCollection = async () => {
    console.log("collectionId", collectionId);
    await deleteCollection(collectionId);
    alert("Collection deleted successfully!");
    navigate("/my-collection");
  };

  return (
    <PageContainer>
      {collection && (
        <>
          <CollectionTitleSection
            isMobile={isMobile}
            collection={collection}
            setModalOpen={setModalOpen}
            handleDeleteCollection={handleDeleteCollection}
            isMe={isMe}
          />
          {isMe && (
            <CollectionKeyMetricSummarySection
              isXL={isMobile}
              isSM={isMobile}
              metrics={metrics}
              views={metrics.views}
              likes={metrics.likes}
              replies={metrics.replies}
              reposts={metrics.reposts}
              numOfThreads={collection.threadList.length}
            />
          )}
          {isMobile && (
            <Button
              variant={"outline"}
              onClick={() => setIsVisible(!isVisible)}
            >
              {t("myCollection.showTableOfContents")}
            </Button>
          )}
          <CardContainerRetro
            weight="bold"
            rd={16}
            w={"100%"}
            bg={colorsCustom.paper}
          >
            <Flex w={"100%"} justify={"flex-start"} p={isMobile ? 0 : 48}>
              <CollectionContentOfTable
                collection={collection}
                selectedThread={selectedThread}
                setSelectedThread={setSelectedThread}
                isMobile={isMobile}
                t={t}
                isVisible={isVisible}
                setIsVisible={setIsVisible}
              />
              {selectedThread && (
                <CollectionThreadContent
                  selectedThread={selectedThread}
                  setSelectedThread={setSelectedThread}
                  threadList={collection.threadList}
                  isMobile={isMobile}
                  collectionID={collectionId}
                />
              )}
            </Flex>
          </CardContainerRetro>
        </>
      )}
      {modalOpen && (
        <UpdateCollectionModal
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          title={title}
          setTitle={setTitle}
          description={description}
          setDescription={setDescription}
          threadsList={lifetimeThreadsList}
          selectedThreads={selectedThreads}
          handleThreadSelect={handleThreadSelect}
          isMobile={isMobile}
          onUpdateCollection={handleCreateCollection}
          collectionList={collectionList}
          handleRemoveThread={handleRemoveThread}
          isNew={false}
        />
      )}
    </PageContainer>
  );
};

export default CollectionPage;
